import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { TherapyTool, accessEndsAt, centerState } from "../../domain/types";
import AdoptButton from "./AdoptButton";
import CenterManagedNotice from "./CenterManagedNotice";
import ToolLabels from "./ToolLabels";
import { toolName, toolShortDescription } from "../toolCopy";
import { formatDay, toolPriceLabel } from "../price";
import { useBillingPeriod } from "../../../subscription";
import { iconFor } from "../toolIcons";

interface ToolCardProps {
    tool: TherapyTool;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const Icon = iconFor(tool.icon);
    // Dropped, and theirs until the period that paid for it runs out.
    const endsAt = accessEndsAt(tool);

    // Priced in the period this account is billed in: a module goes onto the
    // subscription that is already running, so a yearly plan pays the yearly
    // price for it.
    const period = useBillingPeriod();
    const priceLabel = toolPriceLabel(t, tool, i18n.language || "es", period);

    return (
        <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm transition-shadow hover:shadow-theme-md dark:border-gray-800 dark:bg-gray-900">
            <button
                type="button"
                onClick={() => navigate(`/tools-catalog/${tool.slug}`)}
                className="text-left"
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-3">
                        <span
                            aria-hidden="true"
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400"
                        >
                            <Icon className="h-5 w-5" />
                        </span>
                        <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-content-primary">
                            {toolName(t, tool)}
                        </h3>
                    </div>
                    <span className="max-w-[50%] shrink-0 truncate rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                        {priceLabel}
                    </span>
                </div>
                <p className="mt-2 text-sm text-content-tertiary">
                    {toolShortDescription(t, tool)}
                </p>
            </button>

            <ToolLabels tool={tool} />

            {/* The action sits on the left, where every card's does. It used
                to be pushed right to make room for a price caption that is
                gone: the chip at the top says the price now. */}
            <div className="mt-4 flex flex-wrap items-center gap-3">
                {tool.disabled_by_center || tool.managed_by_center ? (
                    <CenterManagedNotice compact state={centerState(tool)} />
                ) : (
                    tool.adopted && (
                        <span className="text-xs font-medium text-success-600 dark:text-success-400">
                            {/* A dropped module is still adopted, and the day
                                it runs out belongs with that word rather than
                                under the button. */}
                            {endsAt
                                ? t("marketplace.adoptedUntil", {
                                      date: formatDay(endsAt, i18n.language || "es"),
                                  })
                                : t("marketplace.adopted")}
                        </span>
                    )
                )}
                <AdoptButton tool={tool} />
            </div>
        </div>
    );
};

export default ToolCard;
